import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {finalize, Observable, of, share, tap} from 'rxjs';
import {IconSize} from '../../bfa-api/models/icon-size';
import {API_CONFIGS} from '../../../configs/api.config';
import {IconService as BfaIconService} from '../../bfa-api/services/icon.service';
import {API_URL_PLACEHOLDER} from '../../constants/api-url-placeholder.constant';
import {Icon} from '../../types/icons/icon.type';
import {iconToIconPath} from '../../util/icon-to-icon-path/icon-to-icon-path.util';
import {STATIC_CONFIGS} from '../../../configs/static.config';

@Injectable({
    providedIn: 'root'
})
export class IconService {
    private readonly httpClient: HttpClient;
    private readonly svgCache: Map<string, string>;
    private readonly svgLoadingQueue: Map<string, Observable<string>>;

    public constructor() {
        this.httpClient = inject(HttpClient);
        this.svgCache = new Map<string, string>();
        this.svgLoadingQueue = new Map<string, Observable<string>>();
    }

    public static getUserIconUrl(userId: string, iconSize: IconSize = STATIC_CONFIGS.ui.defaultIconFetchSize): string {
        return API_CONFIGS.paths.userIcons.replace('{userId}', userId) + '?size=' + iconSize;
    }

    public static getVaultIconUrl(vaultId: string, iconSize: IconSize = STATIC_CONFIGS.ui.defaultIconFetchSize): string {
        return API_CONFIGS.paths.vaultIcons.replace('{vaultId}', vaultId) + '?size=' + iconSize;
    }

    public static getIconUrl(iconId: string | undefined, iconSize: IconSize = STATIC_CONFIGS.ui.defaultIconFetchSize): string {
        return iconId ? API_CONFIGS.paths.icons + '/' + iconId + '?size=' + iconSize : '';
    }

    public static getAbsoluteBfaIconUrl(iconId: string, iconSize: IconSize = STATIC_CONFIGS.ui.defaultIconFetchSize): string {
        return API_URL_PLACEHOLDER + '/' + API_CONFIGS.apis.bfa.path + BfaIconService.IconGetIconPath.replace('{iconId}', iconId) + '?size=' + iconSize;
    }

    public getSvg(name: Icon): Observable<string> {
        return this.fetchSvg(iconToIconPath(name));
    }

    public fetchSvg(url: string): Observable<string> {
        const cached = this.svgCache.get(url);
        if (cached !== undefined) {
            return of(cached);
        }
        const inProgress = this.svgLoadingQueue.get(url);
        if (inProgress !== undefined) {
            return inProgress;
        }
        const request = this.httpClient.get(url, { responseType: 'text' })
            .pipe(
                tap(svg => {
                    this.svgCache.set(url, svg);
                }),
                finalize(() => {
                    this.svgLoadingQueue.delete(url);
                }),
                share()
            );
        this.svgLoadingQueue.set(url, request);
        return request;
    }
}
